import { cn } from '@/shared/lib/utils';
import { cva, type VariantProps } from 'class-variance-authority';
import { type ClassValue } from 'clsx';

/**
 * RDS Input 공통 스타일.
 *
 * Figma RDS-v1.0 Input: Filled(둥근 박스) / Outline(하단 라인),
 * Small(버튼 h-9 정렬)·Medium·Large, Default·Hover·Focus·Disabled·상태색.
 * @see https://www.figma.com/design/wcgji99mZRPIytCZDjwqxe/RDS-v1.0?node-id=2420-2934
 */
export const formControlVariants = cva(
  [
    'flex w-full items-center gap-3 font-normal text-ruda-text-primary',
    'transition-[color,background-color,border-color]',
    'placeholder:text-ruda-text-tertiary',
    'focus-visible:outline-none',
    'disabled:cursor-not-allowed disabled:bg-ruda-grey-100',
    'disabled:text-ruda-text-disabled disabled:placeholder:text-ruda-text-disabled',
    // 직접 aria-invalid 또는 상위 group[data-invalid]
    'aria-invalid:border-ruda-error aria-invalid:bg-ruda-error-50',
    'group-data-[invalid=true]:border-ruda-error',
    'group-data-[invalid=true]:bg-ruda-error-50',
  ],
  {
    variants: {
      variant: {
        /** 둥근 보더 박스 (어드민 기본) */
        filled: [
          'border-[1.5px] border-ruda-border-light bg-white shadow-none',
          'hover:border-ruda-border-dark hover:bg-ruda-grey-50',
          'focus-visible:border-ruda-text-primary focus-visible:bg-white',
          'disabled:border-ruda-border-light disabled:hover:bg-ruda-grey-100',
          'aria-invalid:hover:border-ruda-error aria-invalid:hover:bg-ruda-error-50',
          'aria-invalid:focus-visible:border-ruda-error',
          'group-data-[invalid=true]:hover:border-ruda-error',
          'group-data-[invalid=true]:hover:bg-ruda-error-50',
          'group-data-[invalid=true]:focus-visible:border-ruda-error',
        ],
        /** 하단 라인만 */
        outline: [
          'rounded-none border-0 border-b-[1.5px] border-ruda-border-light',
          'bg-transparent shadow-none',
          'hover:border-ruda-border-dark',
          'focus-visible:border-ruda-text-primary focus-visible:bg-transparent',
          'disabled:border-ruda-border-light disabled:bg-transparent',
          'aria-invalid:bg-transparent aria-invalid:hover:bg-transparent',
          'group-data-[invalid=true]:bg-transparent',
          'group-data-[invalid=true]:hover:bg-transparent',
        ],
      },
      size: {
        /**
         * Small — Button default(h-9)와 동일 높이. radius 12 (RDS sm)
         */
        sm: 'h-9 rounded-12 px-3 py-0 text-sm leading-5 tracking-tight',
        /** Medium — py 12, 14px, radius 16 */
        md: 'h-auto rounded-16 px-3 py-3 text-sm leading-[22px] tracking-tight',
        /** Large — py 16, 16px, radius 20 */
        lg: 'h-auto rounded-20 px-3 py-4 text-base leading-6 tracking-tight',
      },
      tone: {
        default: '',
        success: [
          'border-ruda-success bg-ruda-success-50',
          'hover:border-ruda-success hover:bg-ruda-success-50',
          'focus-visible:border-ruda-success',
        ],
        info: [
          'border-ruda-info bg-ruda-info-50',
          'hover:border-ruda-info hover:bg-ruda-info-50',
          'focus-visible:border-ruda-info',
        ],
        warning: [
          'border-ruda-warning bg-ruda-warning-50',
          'hover:border-ruda-warning hover:bg-ruda-warning-50',
          'focus-visible:border-ruda-warning',
        ],
        error: [
          'border-ruda-error bg-ruda-error-50',
          'hover:border-ruda-error hover:bg-ruda-error-50',
          'focus-visible:border-ruda-error',
        ],
      },
    },
    compoundVariants: [
      {
        variant: 'outline',
        size: 'sm',
        class: 'h-9 rounded-none',
      },
      {
        variant: 'outline',
        size: 'md',
        class: 'rounded-none',
      },
      {
        variant: 'outline',
        size: 'lg',
        class: 'rounded-none',
      },
      {
        variant: 'outline',
        tone: 'success',
        class: 'bg-transparent hover:bg-transparent',
      },
      {
        variant: 'outline',
        tone: 'info',
        class: 'bg-transparent hover:bg-transparent',
      },
      {
        variant: 'outline',
        tone: 'warning',
        class: 'bg-transparent hover:bg-transparent',
      },
      {
        variant: 'outline',
        tone: 'error',
        class: 'bg-transparent hover:bg-transparent',
      },
    ],
    defaultVariants: {
      variant: 'filled',
      /** 어드민 기본 — Button default(h-9)와 정렬 */
      size: 'sm',
      tone: 'default',
    },
  },
);

export type FormControlVariantProps = VariantProps<typeof formControlVariants>;

/** @deprecated formControlVariants 사용 — 하위 호환 */
export const formControlStyles = formControlVariants({
  variant: 'filled',
  size: 'sm',
});

/** @deprecated 고정 높이 대신 size variant의 padding 사용 */
export const formControlHeight = '';

export const formFieldControlClassName = formControlVariants({
  variant: 'filled',
  size: 'sm',
});

export function formControlClassName(
  ...extra: Array<ClassValue | FormControlVariantProps>
) {
  const variantProps: FormControlVariantProps = {};
  const classNames: ClassValue[] = [];

  for (const item of extra) {
    if (
      item &&
      typeof item === 'object' &&
      !Array.isArray(item) &&
      ('variant' in item || 'size' in item || 'tone' in item)
    ) {
      Object.assign(variantProps, item);
    } else {
      classNames.push(item as ClassValue);
    }
  }

  return cn(formControlVariants(variantProps), ...classNames);
}

/** Label — RDS Body/15, text-secondary-grey */
export const formLabelClassName =
  'text-[15px] font-normal leading-6 tracking-tight text-ruda-text-secondary';

/** Helper / description — RDS Body/14, text-tertiary */
export const formHelperClassName =
  'text-sm font-normal leading-[22px] tracking-tight text-ruda-text-tertiary';

/** Error helper — text-error */
export const formErrorClassName =
  'text-sm font-normal leading-[22px] tracking-tight text-[#d93e39]';

/** 필수 항목 라벨 `*` — FormLabel / Field 공통 */
export const formRequiredMarkClassName = 'ml-0.5 text-[#d93e39]';

/** Label↔Input↔Helper 간격 4px */
export const formFieldGapClassName = 'flex flex-col gap-1';
